"use client"
import * as React from "react";
import RouterLink from "next/link";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import authImage from "../assets/image/profitpilot.png";
import Image from "next/image";
import styled from '@emotion/styled';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

export interface LayoutProps {
  children: React.ReactNode;
}

const Title = styled.h1`
  text-align: center;
  color: #2c3e50;
  font-size: 2.5rem;
  margin-bottom: 20px;
`;

export function Layout({ children }: LayoutProps): React.JSX.Element {
  const [isMounted, setIsMounted] = React.useState(false);
  const sliderRef = React.useRef<Slider>(null);

  React.useEffect(() => {
    setIsMounted(true);
  }, []);
  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: false,
    autoplaySpeed: 5000,
    arrows: false,
    appendDots: (dots: any) => (
      <div style={{ bottom: "-40px" }}>
        <ul style={{ margin: "0px" }}> {dots} </ul>
      </div>
    ),
  };

  return (
    <React.Suspense fallback={<div>loading.....</div>}>
      <Box
        sx={{
          display: { xs: "flex", lg: "grid" },
          flexDirection: "column",
          gridTemplateColumns: "1fr 1fr",
          minHeight: "100vh",
        }}
      >
        <Box
          sx={{ display: "flex", flex: "1 1 auto", flexDirection: "column" }}
        >
          <Box sx={{ p: 3 }}>
            <Box
              component={RouterLink}
              href="/"
              sx={{ display: "inline-block", fontSize: 0 }}
            >
              <div>LOGO</div>
            </Box>
          </Box>
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              flex: "1 1 auto",
              justifyContent: "center",
              p: { xs: 2, md: 3 },
            }}
          >
            <Box sx={{ maxWidth: "450px", width: "100%" }}>{children}</Box>
          </Box>
        </Box>
        <Box
          sx={{
            alignItems: "center",
            background:
              "radial-gradient(50% 50% at 50% 50%, #122647 0%, #090E23 100%)",
            color: "var(--mui-palette-common-white)",
            display: { xs: "none", lg: "flex" },
            justifyContent: "center",
            p: 3,
          }}
        >
          <Stack spacing={3}>
            <Stack spacing={1}>
              <Typography
                color="inherit"
                sx={{
                  fontSize: "24px",
                  lineHeight: "32px",
                  textAlign: "center",
                  color: "white",
                }}
                variant="h1"
              >
                {"Welcome to "}
                <Box component="span" sx={{ color: "#15b79e" }}>
                  Profit Pilot!
                </Box>
              </Typography>
            </Stack>
            <Box sx={{ width: "100%", maxWidth: "800px", px: 4, mt: 4 }}>
              {isMounted && (
                <>
                  <Slider ref={sliderRef} {...sliderSettings}>
                    {/* Slide 1: YouTube Video */}
                    <div>
                      <Box sx={{ display: "flex", justifyContent: "center", height: "400px", alignItems: "center", px: 2 }}>
                        <iframe
                          width="100%"
                          height="100%"
                          src="https://www.youtube.com/embed/hEYQJQHldYo?autoplay=0&mute=1&rel=0"
                          title="YouTube video player"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          style={{ borderRadius: "12px", boxShadow: "0 10px 30px rgba(0,0,0,0.5)" }}
                        ></iframe>
                      </Box>
                    </div>

                    {/* Slide 2: Image */}
                    <div>
                      <Box sx={{ display: "flex", justifyContent: "center", height: "400px", alignItems: "center" }}>
                        <Image
                          alt="Profit Pilot Illustration"
                          src={authImage}
                          style={{ height: "100%", width: "auto", objectFit: "contain" }}
                        />
                      </Box>
                    </div>
                  </Slider>

                  <Stack direction="row" spacing={3} justifyContent="center" sx={{ mt: 7 }}>
                    <IconButton
                      onClick={() => sliderRef.current?.slickPrev()}
                      sx={{ color: "white", backgroundColor: "rgba(255,255,255,0.1)", '&:hover': { backgroundColor: "rgba(255,255,255,0.3)" } }}
                    >
                      <ArrowBackIosNewIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => sliderRef.current?.slickNext()}
                      sx={{ color: "white", backgroundColor: "rgba(255,255,255,0.1)", '&:hover': { backgroundColor: "rgba(255,255,255,0.3)" } }}
                    >
                      <ArrowForwardIosIcon />
                    </IconButton>
                  </Stack>
                </>
              )}
            </Box>
          </Stack>
        </Box>
      </Box>
    </React.Suspense>
  );
}
